import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { type UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { SettingsSection } from "@src/appV2/redesign/components/SettingsSection";

import { AppInfoSectionContainer } from "../sections/AppInfo/AppInfoSectionContainer";
import { CalendarDisplaySectionContainer } from "../sections/CalendarDisplay/CalendarDisplaySectionContainer";
import { ComplianceSectionContainer } from "../sections/Compliance/ComplianceSectionContainer";
import { JobManagementSectionContainer } from "../sections/JobManagement/JobManagementSectionContainer";
import { RateBillingSectionContainer } from "../sections/RateBilling/RateBillingSectionContainer";
import { SchedulingSectionContainer } from "../sections/Scheduling/SchedulingSectionContainer";
import { TimeAttendanceSectionContainer } from "../sections/TimeAttendance/TimeAttendanceSectionContainer";
import { TranslationSectionContainer } from "../sections/Translation/TranslationSectionContainer";
import { WorkerCredentialsSectionContainer } from "../sections/WorkerCredentials/WorkerCredentialsSectionContainer";
import { SETTINGS_SECTIONS, type SettingsPermissions, type SettingsSectionConfig } from "../types";

interface ActiveSettingsSectionContentProps {
  activeSection: SettingsSectionConfig;
  facilityProfile: UseGetFacilityProfileResponse | undefined;
  guardSettingsAction: (action: () => void) => void;
  hasAdminRole: (role: string) => boolean;
  permissions: SettingsPermissions;
  workplace: WorkplaceProfileResponse | undefined;
  workplaceId: string;
  onSettingsChanged: () => void;
}

export function ActiveSettingsSectionContent(props: ActiveSettingsSectionContentProps) {
  const {
    activeSection,
    facilityProfile,
    guardSettingsAction,
    hasAdminRole,
    permissions,
    workplace,
    workplaceId,
    onSettingsChanged,
  } = props;
  const workplaceAttributes = workplace?.data?.attributes;

  return (
    <SettingsSection
      description={activeSection.description}
      hideHeader={activeSection.id === SETTINGS_SECTIONS.translation}
      title={activeSection.title}
    >
      {activeSection.id === SETTINGS_SECTIONS.timeAndAttendance && (
        <TimeAttendanceSectionContainer
          facilityProfile={facilityProfile}
          guardSettingsAction={guardSettingsAction}
          permissions={permissions}
          workplaceId={workplaceId}
          workplaceName={workplaceAttributes?.name ?? ""}
          onSettingsChanged={onSettingsChanged}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.workerCredentials && (
        <WorkerCredentialsSectionContainer
          guardSettingsAction={guardSettingsAction}
          permissions={permissions}
          workerCredentialConfiguration={workplaceAttributes?.workerCredentialConfiguration}
          workplaceId={workplaceId}
          onSettingsChanged={onSettingsChanged}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.scheduling && (
        <SchedulingSectionContainer
          facilityProfile={facilityProfile}
          guardSettingsAction={guardSettingsAction}
          hasAdminRole={hasAdminRole}
          permissions={permissions}
          workplace={workplace}
          workplaceId={workplaceId}
          onSettingsChanged={onSettingsChanged}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.compliance && (
        <ComplianceSectionContainer
          guardSettingsAction={guardSettingsAction}
          permissions={permissions}
          workplaceAttributes={workplaceAttributes}
          workplaceId={workplaceId}
          onSettingsChanged={onSettingsChanged}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.rateAndBilling && (
        <RateBillingSectionContainer
          facilityProfile={facilityProfile}
          guardSettingsAction={guardSettingsAction}
          permissions={permissions}
          workplaceId={workplaceId}
          onSettingsChanged={onSettingsChanged}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.jobManagement && (
        <JobManagementSectionContainer workplaceId={workplaceId} />
      )}
      {activeSection.id === SETTINGS_SECTIONS.calendarDisplay && (
        <CalendarDisplaySectionContainer
          guardSettingsAction={guardSettingsAction}
          permissions={permissions}
          timezone={facilityProfile?.tmz ?? "America/Los_Angeles"}
          workplaceId={workplaceId}
        />
      )}
      {activeSection.id === SETTINGS_SECTIONS.translation && <TranslationSectionContainer />}
      {activeSection.id === SETTINGS_SECTIONS.appInfo && <AppInfoSectionContainer />}
    </SettingsSection>
  );
}
